import { useState, useRef, useCallback } from 'react';
import { AGENT_URL, getAgentToken } from '../utils/agentFetch';

export interface AgentUsage {
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  cost_gbp: number;
  llm: string;
}

export interface SendOptions {
  message: string;
  filters: string[];
  llm: string;
  history: Array<{ role: string; content: string }>;
}

interface StreamState {
  streamText: string;
  isStreaming: boolean;
  usage: AgentUsage | null;
  spendWarning: string | null;
  error: string | null;
}

export function useAgentStream() {
  const [state, setState] = useState<StreamState>({
    streamText: '',
    isStreaming: false,
    usage: null,
    spendWarning: null,
    error: null,
  });
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const rawTextRef = useRef('');

  const abort = useCallback(() => {
    readerRef.current?.cancel();
    setState(s => ({ ...s, isStreaming: false }));
  }, []);

  const send = useCallback(async (
    opts: SendOptions,
    callbacks?: {
      onDone?: (rawText: string, usage: AgentUsage | null, spendWarning: string | null) => void;
      onError?: (code: string, message: string) => void;
    },
  ) => {
    rawTextRef.current = '';
    setState({ streamText: '', isStreaming: true, usage: null, spendWarning: null, error: null });

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = getAgentToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${AGENT_URL}/api/chat/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify(opts),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const msg = errData.error || `HTTP ${response.status}`;
        setState(s => ({ ...s, isStreaming: false, error: msg }));
        callbacks?.onError?.(String(response.status), msg);
        return;
      }

      const reader = response.body!.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop()!;

        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          let event: any;
          try { event = JSON.parse(part.slice(6)); } catch { continue; }

          if (event.type === 'text') {
            rawTextRef.current += event.chunk;
            setState(s => ({ ...s, streamText: rawTextRef.current }));
          } else if (event.type === 'done') {
            const usage = event.usage || null;
            const spendWarning = event.spend_warning || null;
            setState(s => ({ ...s, isStreaming: false, usage, spendWarning }));
            callbacks?.onDone?.(rawTextRef.current, usage, spendWarning);
          } else if (event.type === 'error') {
            const code = event.code || 'error';
            const msg = event.message || 'Something went wrong.';
            setState(s => ({ ...s, isStreaming: false, error: msg }));
            callbacks?.onError?.(code, msg);
          }
        }
      }
    } catch (err: any) {
      const hasPartial = !!rawTextRef.current;
      const msg = hasPartial
        ? 'The connection was interrupted. Response may be incomplete.'
        : 'The connection was lost. Please try again.';
      setState(s => ({ ...s, isStreaming: false, error: msg }));
      callbacks?.onError?.('CONNECTION_ERROR', msg);
    }
  }, []);

  return { ...state, send, abort, agentUrl: AGENT_URL };
}
